import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import type { User, AuthContextType, Nursery, RolePermissions } from '../types/auth';
import { authAPI, nurseryAPI, clearCache } from '../utils/api';
import { isNurseryRoute } from '../utils/navigation';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Only log in development
const isDev = import.meta.env.DEV;
const log = (message: string, ...args: any[]) => {
  if (isDev) console.log(message, ...args);
};

const logError = (message: string, ...args: any[]) => {
  if (isDev) console.error(message, ...args);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nurseries, setNurseries] = useState<Nursery[]>([]);
  const [currentNursery, setCurrentNurseryState] = useState<Nursery | null>(null);
  const [nurseriesLoading, setNurseriesLoading] = useState(false);
  
  // Role management state
  const [userRolePermissions, setUserRolePermissions] = useState<RolePermissions | null>(null);
  const [userRolePermissionsLoading, setUserRolePermissionsLoading] = useState(false);

  const permissionRequestRef = useRef(0);
  const [permissionsNurseryId, setPermissionsNurseryId] = useState<string | null>(null);

  // Ref so loadNurseries can read current nursery without it being a dependency
  // (avoids loadNurseries re-creation when currentNursery is set inside loadNurseries, which caused 2–3× /nurseries calls)
  const currentNurseryRef = useRef<Nursery | null>(null);
  currentNurseryRef.current = currentNursery;

  // Load nurseries when user is authenticated
  const loadNurseries = useCallback(async () => {
    try {
      log('🏢 Loading nurseries...');
      setNurseriesLoading(true);
      
      log('🔍 Making API call to /nurseries');
      const response = await nurseryAPI.getNurseries();
      
      log('📡 Nurseries API Response:', response);
      log('✅ Response success:', response.success);
      log('📋 Response data:', response.data);
      log('📊 Number of nurseries:', response.data?.length || 0);
      
      if (response.success && response.data) {
        setNurseries(response.data);
        log('💾 Set nurseries in state:', response.data);
        
        // Only auto-select if we don't have a current nursery and we're not on a nursery-specific URL
        if (response.data.length > 0 && !currentNurseryRef.current) {
          log('🎯 Checking if nursery auto-selection is needed...');
          
          // Check if we're currently on a nursery-specific route
          const isOnNurseryRoute = isNurseryRoute(window.location.pathname);
          
          if (!isOnNurseryRoute) {
            // Only auto-select when not on a nursery route
            const storedNursery = localStorage.getItem('currentNursery');
            if (storedNursery) {
              try {
                const parsedNursery = JSON.parse(storedNursery);
                log('💾 Found stored nursery:', parsedNursery);
                // Verify the stored nursery is still in the list
                const validNursery = response.data.find(c => c.nursery_id === parsedNursery.nursery_id);
                if (validNursery) {
                  log('✅ Stored nursery is valid, using it:', validNursery);
                  setCurrentNurseryState(validNursery);
                } else {
                  log('❌ Stored nursery not found, using first available:', response.data[0]);
                  // Stored nursery is no longer valid, select first one
                  setCurrentNurseryState(response.data[0]);
                  localStorage.setItem('currentNursery', JSON.stringify(response.data[0]));
                }
              } catch {
                log('❌ Invalid stored nursery format, using first available:', response.data[0]);
                // Invalid stored nursery, select first one
                setCurrentNurseryState(response.data[0]);
                localStorage.setItem('currentNursery', JSON.stringify(response.data[0]));
              }
            } else {
              log('🆕 No stored nursery, using first available:', response.data[0]);
              // No stored nursery, select first one
              setCurrentNurseryState(response.data[0]);
              localStorage.setItem('currentNursery', JSON.stringify(response.data[0]));
            }
          } else {
            log('🔗 On nursery route, skipping auto-selection');
          }
        } else if (response.data.length === 0) {
          log('⚠️ No nurseries found in response');
        }
      } else {
        log('❌ API response indicates failure or no data');
        log('   - success:', response.success);
        log('   - data exists:', !!response.data);
      }
    } catch (error: any) {
      logError('🚨 Failed to load nurseries:', error);
      logError('🔍 Error details:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
    } finally {
      setNurseriesLoading(false);
      log('🏁 Nurseries loading finished');
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in on app start
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('jwttoken');
    
    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser);
        log('👤 Setting user from localStorage:', parsedUser);
        setUser(parsedUser);
        // Load nurseries after setting user
        log('🚀 User set, now loading nurseries...');
        loadNurseries();
      } catch (error) {
        // Clear invalid data
        localStorage.removeItem('jwttoken');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('currentNursery');
      }
    }
    setLoading(false);
  }, [loadNurseries]);



  const login = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      // Clear all cached data before login to ensure fresh data for new user
      log('🧹 Clearing all cached data before login...');
      clearCache();
      
      const response = await authAPI.login({ email, password });
      
      const loginSucceeded = response.success === true || response.success === 1;
      const token = response.user?.jwttoken;

      if (loginSucceeded && response.user && token) {
        // Store user data and tokens
        localStorage.setItem('user', JSON.stringify(response.user));
        localStorage.setItem('jwttoken', token);
        if (response.refresh_token) {
          localStorage.setItem('refresh_token', response.refresh_token);
        }
        
        setUser(response.user);
        setError(null); // Clear any errors on success
        
        // Load nurseries after successful login
        log('🚀 Login successful, now loading nurseries...');
        await loadNurseries();
        
      } else {
        const errorMessage = response.success ? 'Login response did not include a user token.' : 'Invalid email or password. Please check your credentials and try again.';
        
        // Set error synchronously
        setError(errorMessage);
        
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      // Handle different types of API errors
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        if (status === 401 || status === 403) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (status === 422) {
          errorMessage = 'Invalid email or password format. Please check your credentials.';
        } else if (status === 404) {
          errorMessage = 'Login service not found. Please contact support.';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.errors) {
          errorMessage = Array.isArray(errorData.errors) 
            ? errorData.errors.join(', ') 
            : 'Validation error. Please check your input.';
      }
      } else if (error.request) {
        // This is likely a CORS or network connectivity issue
        if (error.code === 'ERR_NETWORK') {
          errorMessage = 'Unable to connect to the server. This may be due to network issues or server configuration. Please contact support if the problem persists.';
        } else {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        }
      } else if (error.message) {
        // Use the error message if available (like our own thrown errors)
        errorMessage = error.message;
      }
      
      // Set error before throwing to ensure it's set
      setError(errorMessage);
      
      // Then throw
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loadNurseries]);

  const logout = useCallback(() => {
    // Clear all cached data on logout
    log('🧹 Clearing all cached data on logout...');
    clearCache();
    
    localStorage.removeItem('user');
    localStorage.removeItem('jwttoken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('currentNursery');
    permissionRequestRef.current += 1;
    setUserRolePermissions(null);
    setPermissionsNurseryId(null);
    setUserRolePermissionsLoading(false);
    setUser(null);
    setCurrentNurseryState(null);
    setNurseries([]);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load user role permissions for a specific nursery
  const loadUserRolePermissions = useCallback(async (nurseryId: string) => {
    if (!user) return;
    const requestId = ++permissionRequestRef.current;
    setUserRolePermissions(null);
    setPermissionsNurseryId(null);
    try {
      setUserRolePermissionsLoading(true);
      log('🔐 Loading user role permissions for nursery:', nurseryId);
      
      const response = await authAPI.getUserNurseryAssignment(nurseryId, user.id);
      if (requestId !== permissionRequestRef.current) return;
      setUserRolePermissions(response.role.role);
      setPermissionsNurseryId(nurseryId);
      log('✅ User role permissions loaded:', response.role.role);
    } catch (error: any) {
      logError('❌ Failed to load user role permissions:', error);
      if (requestId !== permissionRequestRef.current) return;
      // If no assignment found, clear permissions (user has no role in this nursery)
      setUserRolePermissions(null);
    } finally {
      if (requestId === permissionRequestRef.current) setUserRolePermissionsLoading(false);
    }
  }, [user]);

  const permissionAliases: Record<string, string[]> = {
    edge_devices: ['edgeDevices'],
    edgeDevices: ['edge_devices'],
    attendance: ['attendanceRecord', 'attendanceByDate'],
    attendanceRecord: ['attendance'],
    reports: ['childrenAggregate', 'facesImages', 'childTimeline'],
    childrenAggregate: ['reports'],
    facesImages: ['reports'],
    childTimeline: ['reports'],
    vacations: ['childrenVacation'],
    childrenVacation: ['vacations'],
    consent: ['consentForms', 'consents'],
    consentForms: ['consent', 'consents'],
    consents: ['consent', 'consentForms'],
  };

  const getPermissionEntry = useCallback((resource: string) => {
    if (!userRolePermissions || permissionsNurseryId !== currentNursery?.nursery_id) return undefined;
    const keys = [resource, ...(permissionAliases[resource] || [])];
    return keys.map((key) => userRolePermissions[key]).find(Boolean);
  }, [userRolePermissions, permissionsNurseryId, currentNursery?.nursery_id]);

  const getScope = useCallback((resource: string): 'all' | 'assigned' => {
    if (user?.superAdminFlag === 1) {
      return 'all';
    }

    if (!userRolePermissions) {
      return 'all';
    }

    const rp = getPermissionEntry(resource);
    if (!rp || Array.isArray(rp)) {
      return 'all';
    }

    return rp.scope || 'all';
  }, [user, userRolePermissions, getPermissionEntry]);

  // Check if user has permission for a specific resource and action
  const hasPermission = useCallback((resource: string, action: string): boolean => {
    // Super admins have access to everything
    if (user?.superAdminFlag === 1) {
      return true;
    }
    
    // If no role permissions loaded, deny access
    if (!userRolePermissions) {
      return false;
    }
    
    // Check if the resource exists and if the action is allowed
    const resourcePermissions = getPermissionEntry(resource);
    if (!resourcePermissions) {
      return false;
    }

    const actions = Array.isArray(resourcePermissions)
      ? resourcePermissions
      : resourcePermissions.actions;

    return actions.includes(action);
  }, [user, userRolePermissions, getPermissionEntry]);

  const setCurrentNursery = useCallback((nursery: Nursery) => {
    currentNurseryRef.current = nursery;
    setCurrentNurseryState(nursery);
    localStorage.setItem('currentNursery', JSON.stringify(nursery));
    
    // Load user role permissions when nursery changes
    if (user) {
      loadUserRolePermissions(nursery.nursery_id);
    }
  }, [user, loadUserRolePermissions]);

  // Load role permissions when current nursery changes
  useEffect(() => {
    if (user && currentNursery) {
      loadUserRolePermissions(currentNursery.nursery_id);
    }
  }, [user, currentNursery, loadUserRolePermissions]);

  const value: AuthContextType = {
    user,
    loading,
    error,
    nurseries,
    currentNursery,
    nurseriesLoading,
    login,
    logout,
    clearError,
    setCurrentNursery,
    userRolePermissions,
    userRolePermissionsLoading,
    loadUserRolePermissions,
    hasPermission,
    getScope,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 
